import React from 'react';
import { BellIcon, ChatBubbleLeftRightIcon, ExclamationCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const NotificationPanel = ({ 
    notifications, 
    unreadCount, 
    loading, 
    fetchingError,
    onMarkAllRead,
    onNotificationClick,
    fetchNotifications,
    panelRef // Pass the ref from the hook
}) => {
  return (
    <div 
      ref={panelRef} // Attach the ref here
      className="absolute right-0 mt-2 w-80 bg-gray-800 rounded-md shadow-lg z-50 ring-1 ring-black ring-opacity-5 max-h-96 overflow-y-auto"
    >
      <div className="p-3 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800 z-10">
        <h3 className="text-white font-medium">Notifications</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => fetchNotifications()}
            className="text-gray-400 hover:text-white transition-colors"
            title="Refresh notifications"
            disabled={loading}
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          
          {unreadCount > 0 && (
            <button 
              onClick={onMarkAllRead}
              className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading} // Disable while loading or if count is 0
            >
              Mark all as read
            </button>
          )}
        </div>
      </div>
      
      {loading && notifications.length === 0 ? (
        <div className="p-4 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : fetchingError ? (
        <div className="p-4 text-center">
          <div className="flex items-center justify-center text-red-400 mb-2">
            <ExclamationCircleIcon className="h-5 w-5 mr-2" />
            <span>Error loading notifications</span>
          </div>
          <button
            onClick={() => fetchNotifications()}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            Try again
          </button>
        </div>
      ) : notifications.length === 0 ? (
        <div className="p-4 text-center text-gray-400">
          No notifications
        </div>
      ) : (
        <div className="divide-y divide-gray-700">
          {notifications.map(notification => (
            <div 
              key={notification._id}
              onClick={() => onNotificationClick(notification)}
              className={`p-3 hover:bg-gray-700 cursor-pointer ${!notification.read ? 'bg-gray-700/50' : ''}`}
            >
              <div className="flex items-start">
                {notification.type === 'sms' ? (
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                ) : (
                  <BellIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0"> {/* Added min-w-0 for proper truncation */} 
                  <p className="text-sm font-medium text-white truncate"> {/* Added truncate */} 
                    {notification.type === 'sms' 
                      ? `New message from ${notification.sender || 'Unknown'}` 
                      : notification.title || 'New Notification'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                    {notification.content || ''} {/* Handle potentially missing content */} 
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(notification.createdAt).toLocaleString()}
                  </p>
                </div>
                {!notification.read && (
                  <span className="ml-2 h-2 w-2 bg-blue-500 rounded-full flex-shrink-0 self-center"></span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel; 
